import type { Timeline } from "../types/timeline";

/**
 * CANONICAL DEFINITION of the composition canvas background.
 *
 * The background painted behind letterboxed clips, timeline gaps and any frame
 * smaller than the canvas is a property of the COMPOSITION, never of the editor
 * UI theme. Every renderer (export, preview, thumbnails, fallback frames) must
 * resolve it through the helpers below so a frame looks identical wherever it
 * is drawn. Reading a CSS theme token (`--screen-bg` and friends) to fill the
 * rendered canvas is always wrong: it makes the exported file depend on whether
 * the user happened to be in light or dark mode.
 *
 * The three states of `Timeline.backgroundFillMode`, all part of the shared
 * cross-platform project JSON schema:
 *   undefined -> solid DEFAULT_CANVAS_BACKGROUND_COLOR bars
 *   "color"   -> solid `layoutBackgroundColor` (falls back to the default when
 *                no color has been picked yet)
 *   "blur"    -> DEFAULT_CANVAS_BACKGROUND_COLOR first, then a blurred
 *                cover-fit copy of the base clip painted on top. The blurred
 *                backdrop needs the decoded base frame, so callers draw it
 *                themselves; this module only reports that it is required.
 *
 * Editor chrome (the area around the canvas) stays theme-colored and is drawn
 * outside the rendered surface.
 */
export const DEFAULT_CANVAS_BACKGROUND_COLOR = "#000000";

type CanvasBackgroundSource = Pick<
  Timeline,
  "backgroundFillMode" | "layoutBackgroundColor"
>;

/**
 * Solid color to fill the whole canvas with before any clip is composited.
 */
export function resolveCanvasBackgroundColor(
  timeline: CanvasBackgroundSource | undefined | null,
): string {
  if (
    timeline?.backgroundFillMode === "color" &&
    timeline.layoutBackgroundColor
  ) {
    return timeline.layoutBackgroundColor;
  }
  return DEFAULT_CANVAS_BACKGROUND_COLOR;
}

/**
 * Whether the caller must paint a blurred cover-fit copy of the base clip on
 * top of the solid fill returned by resolveCanvasBackgroundColor.
 */
export function canvasBackgroundNeedsBlurBackdrop(
  timeline: CanvasBackgroundSource | undefined | null,
): boolean {
  return timeline?.backgroundFillMode === "blur";
}
